import { useEffect, useState, useCallback, useRef } from 'react';
import { isAfter } from 'date-fns';

interface Event {
  dateStart: string;
}

interface UseCalendarNavigationReturn {
  currentDate: Date;
  days: Date[];
  calendarStartDate: Date;
  calendarEndDate: Date;
  navigatePrevious: () => void;
  navigateNext: () => void;
  navigateToToday: () => void;
  handleDateFromFilterChange: (date: string) => void;
}

export function useCalendarNavigation(
  daysToShow: number,
  initialEvents: Event[],
  allEvents: Event[],
  isInitialized: boolean,
): UseCalendarNavigationReturn {
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [days, setDays] = useState<Date[]>([]);
  const [calendarStartDate, setCalendarStartDate] = useState<Date>(new Date());
  const [calendarEndDate, setCalendarEndDate] = useState<Date>(new Date());

  // Use a ref to track the last navigation time to prevent rapid clicks
  const lastNavigationTime = useRef<number>(0);

  // Initialize with first upcoming event when events are loaded
  useEffect(() => {
    if (isInitialized || allEvents.length === 0) return;

    // Find the first upcoming event and navigate to it
    const now = new Date();
    const upcomingEvents = allEvents
      .filter(event => isAfter(new Date(event.dateStart), now))
      .sort((a, b) => new Date(a.dateStart).getTime() - new Date(b.dateStart).getTime());

    if (upcomingEvents.length > 0) {
      const firstEventDate = new Date(upcomingEvents[0].dateStart);
      setCurrentDate(firstEventDate);
    }
  }, [allEvents, isInitialized]);

  // Simple debounced navigation functions
  const navigatePrevious = useCallback(() => {

    // Basic debounce - prevent clicking too fast
    const now = Date.now();
    if (now - lastNavigationTime.current < 300) return;
    lastNavigationTime.current = now;

    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() - daysToShow);
      return newDate;
    });
  }, [daysToShow]);

  const navigateNext = useCallback(() => {

    // Basic debounce - prevent clicking too fast
    const now = Date.now();
    if (now - lastNavigationTime.current < 300) return;
    lastNavigationTime.current = now;

    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() + daysToShow);
      return newDate;
    });
  }, [daysToShow]);

  const navigateToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const handleDateFromFilterChange = useCallback((date: string) => {
    setCurrentDate(new Date(date));
  }, []);

  // Update days array when current date changes
  useEffect(() => {

    // Calculate start date (centered on current date)
    const start = new Date(currentDate);
    start.setDate(start.getDate() - Math.floor(daysToShow / 2));
    start.setHours(0, 0, 0, 0);

    // Calculate end date
    const end = new Date(start);
    end.setDate(end.getDate() + daysToShow - 1);
    end.setHours(23, 59, 59, 999);

    // Update state values
    setCalendarStartDate(start);
    setCalendarEndDate(end);

    // Generate days array
    const newDays = Array.from({ length: daysToShow }, (_, i) => {
      const day = new Date(start);
      day.setDate(day.getDate() + i);
      return day;
    });

    setDays(newDays);
  }, [currentDate, daysToShow]);

  return {
    currentDate,
    days,
    calendarStartDate,
    calendarEndDate,
    navigatePrevious,
    navigateNext,
    navigateToToday,
    handleDateFromFilterChange
  };
}
